export type PreparedChatImage = {
  dataUrl: string;
  mimeType: string;
};

const MAX_INPUT_BYTES = 20 * 1024 * 1024;
const MAX_DIMENSION = 1024;
const OUTPUT_QUALITY = 0.8;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Kunde inte läsa bildfilen.'));
    image.src = src;
  });
}

export async function prepareChatImage(file: File): Promise<PreparedChatImage> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Filen är inte en bild — välj ett foto från kameran eller bildbiblioteket.');
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error('Bilden är för stor (max 20 MB).');
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl).catch(() => {
      throw new Error(
        'Kunde inte läsa bildformatet i den här webbläsaren — prova att spara bilden som JPEG eller PNG och försök igen.',
      );
    });
    const largestSide = Math.max(image.naturalWidth, image.naturalHeight);
    if (!largestSide) {
      throw new Error('Kunde inte läsa bildfilen.');
    }
    const scale = Math.min(1, MAX_DIMENSION / largestSide);
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Kunde inte bearbeta bilden i webbläsaren.');
    }
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    const dataUrl = canvas.toDataURL('image/jpeg', OUTPUT_QUALITY);
    if (!dataUrl.startsWith('data:image/jpeg;base64,')) {
      throw new Error('Kunde inte bearbeta bilden i webbläsaren.');
    }
    return { dataUrl, mimeType: 'image/jpeg' };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
